const { isEmpty, orderBy, range } = require('lodash');
const moji = require('moji');

const { toHankaku } = require('../util');

// 全角/半角・カタカナ/ひらがな・大文字小文字・区切り文字の揺れを吸収した比較用の文字列にする
const normalize = (value) => moji(toHankaku(value || ''))
  .convert('HK', 'ZK') // 半角カナ -> 全角カナ
  .convert('KK', 'HG') // カタカナ -> ひらがな
  .toString()
  .toLowerCase()
  .replace(/[\s\-ー－_,.、。]/g, '');

const normalizePhone = (value) => normalize(value).replace(/\D/g, '');

const normalizePostalCode = (value) => normalize(value).replace(/\D/g, '');

// テストで繰り返し使うダミー番号。重複を許す
const testPhones = ['070', '080', '090'].flatMap(prefix => (
  ['11111111', '12345678', '11112222'].map(suffix => prefix + suffix)
));

// 携帯番号のみ重複チェックの対象。固定電話は世帯で共有されるため重複を許す
const isCellPhone = (value) => /^(070|080|090)\d{8}$/.test(normalizePhone(value));

const isPhoneUniquenessRequired = (value) => {
  const normalized = normalizePhone(value);
  return isCellPhone(normalized) && !testPhones.includes(normalized);
};

// レーベンシュタイン距離（for文禁止のため reduce で行を畳み込む）
const editDistance = (a, b) => {
  if(isEmpty(a)) return b.length;
  if(isEmpty(b)) return a.length;

  const initialRow = range(b.length + 1);
  const lastRow = [...a].reduce((previousRow, aChar, aIndex) => (
    [...b].reduce((currentRow, bChar, bIndex) => (
      currentRow.concat(Math.min(
        currentRow[bIndex] + 1,                                        // 挿入
        previousRow[bIndex + 1] + 1,                                   // 削除
        previousRow[bIndex] + (aChar === bChar ? 0 : 1),               // 置換
      ))
    ), [aIndex + 1])
  ), initialRow);

  return lastRow[b.length];
};

// 0〜1 の類似度。1 が完全一致
const stringSimilarity = (a, b) => {
  const [normalizedA, normalizedB] = [normalize(a), normalize(b)];
  if(isEmpty(normalizedA) || isEmpty(normalizedB)) return 0;
  if(normalizedA === normalizedB) return 1;

  const longerLength = Math.max(normalizedA.length, normalizedB.length);
  return 1 - editDistance(normalizedA, normalizedB) / longerLength;
};

// 日本語の氏名は姓が先。スペース区切りがあればその手前、なければ先頭2文字を姓とみなす
const familyNameOf = (displayName) => {
  const trimmed = (displayName || '').trim();
  const [beforeSpace] = trimmed.split(/[\s　]+/);
  return beforeSpace !== trimmed && !isEmpty(beforeSpace) ? beforeSpace : trimmed.slice(0, 2);
};

const familyNameSimilarity = (a, b) => stringSimilarity(familyNameOf(a), familyNameOf(b));

// 各項目の重み。姓の一致を最優先し、次いで住所の細部を見る
const weights = {
  familyName: 3,
  displayName: 2,
  nameKana: 2,
  address: 2,
  city: 1,
  phone: 3,
};

const computeSimilarity = (baseUser, otherUser) => {
  const scores = {
    familyName: familyNameSimilarity(baseUser.displayName, otherUser.displayName),
    displayName: stringSimilarity(baseUser.displayName, otherUser.displayName),
    nameKana: stringSimilarity(baseUser.nameKana, otherUser.nameKana),
    address: stringSimilarity(baseUser.address, otherUser.address),
    city: stringSimilarity(baseUser.city, otherUser.city),
    phone: normalizePhone(baseUser.phone) === normalizePhone(otherUser.phone) && !isEmpty(normalizePhone(baseUser.phone)) ? 1 : 0,
  };

  // 両者が値を持つ項目だけで重み付き平均を取る（片方が空の項目は評価から外す）
  const evaluatableKeys = Object.keys(weights).filter((key) => {
    const [baseValue, otherValue] = key === 'familyName'
      ? [baseUser.displayName, otherUser.displayName]
      : [baseUser[key], otherUser[key]];
    return !isEmpty(normalize(baseValue)) && !isEmpty(normalize(otherValue));
  });
  if(isEmpty(evaluatableKeys)) return { score: 0, scores };

  const totalWeight = evaluatableKeys.reduce((x, key) => x + weights[key], 0);
  const weightedScore = evaluatableKeys.reduce((x, key) => x + scores[key] * weights[key], 0);

  return { score: weightedScore / totalWeight, scores };
};

// 類似とみなす下限。これ未満は表示しない
const similarityThreshold = 0.4;

const computeSimilarUsers = (baseUser, candidateUsers, { threshold = similarityThreshold, limit = 5 } = {}) => {
  const scoredUsers = candidateUsers
    .filter(_ => _.id !== baseUser.id)
    .map(_ => ({ ...computeSimilarity(baseUser, _), user: _ }))
    .filter(_ => _.score >= threshold);

  return orderBy(scoredUsers, 'score', 'desc').slice(0, limit);
};

module.exports = {
  normalize,
  normalizePhone,
  normalizePostalCode,
  testPhones,
  isCellPhone,
  isPhoneUniquenessRequired,
  editDistance,
  stringSimilarity,
  familyNameOf,
  familyNameSimilarity,
  computeSimilarity,
  computeSimilarUsers,
  similarityThreshold,
};
